import { createApprove } from '@exodus/ethereum-api';
import { isEthereumLikeToken } from '@exodus/ethereum-lib';
import { LOGS, ApproveTokensError, GetAllowanceError } from '@exodus/exchange-client-providers';
export class ExchangeApproveTokens {
    #addressProvider;
    #exchangeAssets;
    #feeMonitors;
    #walletAccounts;
    #txSend;
    #tracer;
    #exchangeGetAllowance;
    #createApprove;
    constructor(params) {
        this.#addressProvider = params.addressProvider;
        this.#exchangeAssets = params.exchangeAssets;
        this.#feeMonitors = params.feeMonitors;
        this.#txSend = params.txSend;
        this.#walletAccounts = params.walletAccounts;
        this.#tracer = params.exchangeTracer;
        this.#exchangeGetAllowance = params.exchangeGetAllowance;
        this.#createApprove = params.createApprove ?? createApprove;
    }
    approveTokens = async ({ spenderAddress, assetId, approveAmount, walletAccount, }) => {
        const asset = await this.getAsset({ assetId }).catch(() => null);
        if (!asset) {
            return [];
        }
        const tokenApprovalTxData = await this.buildTokenApprovalTxData({
            spenderAddress,
            assetId,
            approveAmount,
            walletAccount,
            asset,
        });
        const [revokeTokenTxData, approveTokenTxData] = tokenApprovalTxData;
        if (!revokeTokenTxData || !approveTokenTxData) {
            return [];
        }
        const feeData = await this.#feeMonitors.getFeeData({ assetName: asset.baseAssetName });
        const fromAddress = await this.getFromAddress(asset, walletAccount);
        const transactions = [];
        try {
            this.traceMessage(LOGS.REVOKING_ALLOWANCE);
            this.#tracer?.traceTime('ApproveTokens');
            const revokeApproveTxId = await this.approveAndSend({
                spenderAddress,
                walletAccount,
                approveAmount: asset.currency.ZERO,
                fromAddress,
                feeData,
                asset,
                txInput: revokeTokenTxData,
            });
            transactions.push({ type: 'preSetupTransaction', id: revokeApproveTxId });
        }
        catch (err) {
            this.traceMessage(LOGS.FAILED_TO_APPROVE_TOKENS);
            throw new ApproveTokensError({
                message: err.message,
                assetId,
                approvalAddress: spenderAddress,
                amount: asset.currency.ZERO,
            });
        }
        finally {
            this.#tracer?.traceTimeEnd('ApproveTokens');
        }
        try {
            this.traceMessage(LOGS.APPROVING_TOKENS);
            this.#tracer?.traceTime('ApproveTokens');
            const approveTxId = await this.approveAndSend({
                spenderAddress,
                walletAccount,
                approveAmount,
                fromAddress,
                feeData,
                asset,
                txInput: approveTokenTxData,
            });
            transactions.push({ type: 'setupTransaction', id: approveTxId });
        }
        catch (err) {
            this.traceMessage(LOGS.FAILED_TO_APPROVE_TOKENS);
            throw new ApproveTokensError({
                message: err.message,
                assetId,
                approvalAddress: spenderAddress,
                amount: approveAmount,
            });
        }
        finally {
            this.#tracer?.traceTimeEnd('ApproveTokens');
        }
        return transactions;
    };
    buildTokenApprovalTxData = async ({ spenderAddress, asset, assetId, approveAmount, walletAccount, }) => {
        const allowance = await this.getAllowance({
            assetId,
            approvalAddress: spenderAddress,
            walletAccount,
        });
        if (allowance.gte(approveAmount)) {
            return [];
        }
        const revokeTokenTxData = await this.getTxData({
            spenderAddress,
            approveAmount: asset.currency.ZERO,
            asset,
        });
        const approveTokenTxData = await this.getTxData({
            spenderAddress,
            approveAmount,
            asset,
        });
        return [revokeTokenTxData, approveTokenTxData];
    };
    getAllowance = async ({ assetId, approvalAddress, walletAccount, }) => {
        try {
            this.traceMessage(LOGS.CHECKING_ALLOWANCE);
            this.#tracer?.traceTime('GetAllowance');
            return await this.#exchangeGetAllowance.getAllowance({
                assetId,
                approvalAddress,
                walletAccount,
            });
        }
        catch (err) {
            this.traceMessage(LOGS.FAILED_TO_GET_ALLOWANCE);
            throw new GetAllowanceError({
                message: err.message,
                assetId,
                approvalAddress,
            });
        }
    };
    approveAndSend = async ({ spenderAddress, approveAmount, walletAccount, feeData, txInput, asset, fromAddress, }) => {
        const approve = this.#createApprove({
            sendTx: ({ asset, ...args }) => this.#txSend({ assetName: asset, ...args }),
        });
        const { txId } = await approve({
            walletAccount,
            fromAddress,
            spenderAddress,
            asset,
            feeData,
            feeAmount: asset.feeAsset.currency.baseUnit(0),
            approveAmount,
            txInput,
        });
        return txId;
    };
    buildTokenApprovalSimulationTx = async ({ spenderAddress, assetId, approveAmount, walletAccount, chainId, }) => {
        const asset = await this.getAsset({ assetId }).catch(() => null);
        if (!asset) {
            return [];
        }
        const tokenApprovalTxData = await this.buildTokenApprovalTxData({
            spenderAddress,
            assetId,
            approveAmount,
            walletAccount,
            asset,
        });
        if (tokenApprovalTxData.length === 0) {
            return [];
        }
        const fromAddress = await this.getFromAddress(asset, walletAccount);
        const feeData = await this.#feeMonitors.getFeeData({ assetName: asset.baseAssetName });
        return tokenApprovalTxData.map((txData) => ({
            chainId,
            from: fromAddress,
            to: asset.contract.address,
            gasPrice: `0x${feeData.gasPrice?.toBaseNumber().toString(16)}`,
            gas: `0x0`,
            value: '0x0',
            data: `0x${txData.toString('hex')}`,
            nonce: undefined,
            maxPriorityFeePerGas: undefined,
            maxFeePerGas: undefined,
        }));
    };
    async getAsset({ assetId }) {
        const asset = this.#exchangeAssets.getAsset({ assetId });
        if (!isEthereumLikeToken(asset)) {
            throw new TypeError('Only Ethereum-like tokens are supported');
        }
        return asset;
    }
    async getFromAddress(asset, walletAccount) {
        const receiveAddress = await this.#addressProvider.getReceiveAddress({
            assetName: asset.name,
            walletAccount: this.#walletAccounts.get(walletAccount),
        });
        return receiveAddress.toString();
    }
    async getTxData({ asset, spenderAddress, approveAmount }) {
        return asset.contract.approve.build(spenderAddress, approveAmount.toBaseString({ unit: false }));
    }
    traceMessage(message) {
        this.#tracer?.traceMessage(message);
    }
}
const exchangeApproveTokensFactory = (args) => new ExchangeApproveTokens({ ...args });
const exchangeApproveTokensModuleDefinition = {
    id: 'exchangeApproveTokens',
    type: 'module',
    factory: exchangeApproveTokensFactory,
    dependencies: [
        'addressProvider',
        'exchangeAssets',
        'txSend',
        'walletAccounts',
        'feeMonitors',
        'exchangeTracer?',
        'exchangeGetAllowance',
        'createApprove?',
    ],
};
export default exchangeApproveTokensModuleDefinition;
